import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Badge } from "reactstrap";
import { Table, Tooltip } from "antd";
import { useLocation, useParams, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { ColumnType } from "antd/es/table";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import TimeSheetListCreation from "./TimeSheetListCreation";
import TimeSheetsHeader from "../../../components/TimeLog/TimeSheetsHeader";
import { logHoursApis } from "../../../api/TimeLog";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { secondsToHHMM, to12HourTime } from "../../../utils/convertions";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import ProjectPageShell from "../shared/ProjectPageShell";
import { GenericStatusPill } from "../shared/statusColors";

interface TimeSheetsListViewProps {
  taskId?: number | null;
  issueId?: number | null;
}

interface TimeLogListViewProps {
  timeLogId: number;
  projectId: number;
  taskId?: number;
  issueId?: number;
  timeLogTitle?: string;
  projectTitle?: string;
  user: string;
  dailyLogHours: string;
  shiftName: string;
  timePeriod: string;
  billingType: string;
  notes: string;
  createdBy: string;
  createdAt?: string;
  approvalStatus: string;
  rawData?: any;
}

const TimeSheetsListView: FC<TimeSheetsListViewProps> = ({
  taskId,
  issueId,
}) => {
  // console.log(issueId, taskId, "ids");

  const location = useLocation();
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const { projectName: stateProjectName, id: stateProjectId } = location.state || {};

  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const projectId = urlProjectId || stateProjectId || currentProjectId;
  const projectName = stateProjectName || currentProjectName;

  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // Scroll to top when component mounts or when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const methods = useForm({ mode: "onChange" });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;
  const [listViewType, setListViewType] = useState("list");
  const [groupBy, setGroupBy] = useState("allTimeLogs");
  const [modalOpen, setModalOpen] = useState(false);
  const [screenSize, setScreenSize] = useState(0);

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [endOfList, setEndOfList] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],

    total: 0,
  });
  const [searchQuery, setsearchQuery] = useState<string | null>(null);

  const groupByOptions = [
    { key: "allTimeLogs", label: "All Time Logs" },
    { key: "allPending", label: "All Pending" },
    { key: "allApproved", label: "All Approved" },
  ];

  const getLabel = (list: any[], key: string) =>
    list.find((item) => item.key === key)?.label || key;

  const handleSelectTimeLog = (logItem: any) => {
    // Ensure we have the raw API data
    const rawData = logItem.rawData || logItem;

    // Navigate to the time log detail page
    navigate(`/pms/project/timelog-detail/${rawData.timeLogId}`, {
      state: {
        ...location.state,
        projectId: rawData.projectId || projectId,
        projectName: rawData.projectName || projectName,
        taskId: rawData.taskId,
        taskName: rawData.taskName,
        issueId: rawData.issueId,
        issueName: rawData.issueName,
      },
    });
  };

  const columns: ColumnType<TimeLogListViewProps>[] = [
    {
      title: "Log Title",
      dataIndex: "timeLogTitle",
      key: "timeLogTitle",
      fixed: "left",
      width: 180,
      render(text: any, record: any) {
        const rawData = record?.rawData || {};
        const moduleType = rawData?.moduleType || "Task";
        const titleText =
          moduleType === "Issue"
            ? rawData?.issueName || text
            : rawData?.taskName || text;

        return (
          <>
            <Badge
              size="small"
              color={moduleType === "Issue" ? "orange" : "blue"}
            >
              {moduleType}
            </Badge>{" "}
            <Tooltip title={titleText} placement="topLeft">
              <span
                className="inlineTableCellLink"
                style={{
                  display: "inline-block",
                  maxWidth: "120px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  verticalAlign: "middle",
                  cursor: "pointer",
                }}
                onClick={() => handleSelectTimeLog(record)}
              >
                {titleText}
              </span>
            </Tooltip>
          </>
        );
      },
    },

    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 100,
      render: (_, record) => <>{record?.user}</>,
    },
    {
      title: "Log Date",
      dataIndex: "logDate",
      key: "logDate",
      width: 100,
      render: (text) =>
        text && text !== "--" ? dayjs(text).format("DD MMM YYYY") : "--", // Handle empty or fallback value
    },
    {
      title: "Shift Name",
      dataIndex: "shiftName",
      key: "shiftName",
      width: 100,
      render: (_, record) => <>{record?.shiftName}</>,
    },
    {
      title: "Daily Log Hours",
      dataIndex: "dailyLogHours",
      key: "dailyLogHours",
      width: 100,
      render: (_, record) => <>{record?.dailyLogHours}</>,
    },
    {
      title: "Time Period",
      dataIndex: "timePeriod",
      key: "timePeriod",
      width: 150,
      render: (_, record) => <>{record?.timePeriod}</>,
    },
    {
      title: "Billing Type",
      dataIndex: "billingType",
      key: "billingType",
      width: 100, // set fixed width
      render: (_, record) => <>{record?.billingType}</>,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 150,
      render: (_, record) => (
        <Tooltip title={record?.notes || "—"} placement="topLeft">
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "150px",
              cursor: "pointer",
            }}
          >
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
              {record?.notes || "—"}
            </ReactMarkdown>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Approval Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (_, record) =>
        record?.approvalStatus && record.approvalStatus !== "-" ? (
          <GenericStatusPill status={record.approvalStatus} />
        ) : (
          <>{record?.approvalStatus}</>
        ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      fixed: "right",
      render: (text) =>
        text ? dayjs(text).format("DD MMM YYYY, hh:mm A") : "--",
    },
  ];

  const mapTimeLogList = (list: any[]): TimeLogListViewProps[] =>
    list.map((item) => {
      const formatTime = (timeStr: string) => {
        if (!timeStr) return "--:--";
        const [hour, minute] = timeStr.split(":");
        const h = parseInt(hour, 10);
        const ampm = h >= 12 ? "PM" : "AM";
        const formattedHour = h % 12 || 12;
        return `${formattedHour.toString().padStart(2, "0")}:${minute} ${ampm}`;
      };

      const formatHours = (seconds: number) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${h.toString().padStart(2, "0")}:${m
          .toString()
          .padStart(2, "0")}`;
      };

      return {
        timeLogId: item.timeLogId,
        // timeLogTitle: `Time Log #${item.timeLogId}`,
        timeLogTitle:
          item.taskName?.trim() !== ""
            ? item.taskName
            : item.issueName?.trim() !== ""
            ? item.issueName
            : `Time Log #${item.timeLogId}`,
        projectId: item.projectId,
        projectTitle: item.projectName || `Project #${item.projectId}`,
        user: item.user || item.userName || item.createdByName || "—",
        dailyLogHours: formatHours(item.totalWorkHoursInSec || 0),
        timePeriod:
          item.loginTime && item.logoutTime
            ? `${formatTime(item.loginTime)} - ${formatTime(item.logoutTime)}`
            : "--:--",
        billingType: item.billingType || "—",
        notes: item.notes || "—",
        createdBy: item.createdBy || "—",
        createdAt: item.createdAt || "—",
        // Include the raw data for reference
        taskId: item.taskId || null, // Ensure taskId is included
        issueId: item.issueId || null, // Ensure issueId is include
        rawData: item,
        logDate: item.logDate || "--",
        shiftName: item.shiftName || "-",
        approvalStatus: item.approvalStatus || "-",
      };
    });

  const fetchData = React.useCallback(
    async (params: any, append = false) => {
      setLoading(true);
      try {
        const {
          current,
          sortOrder,
          pageSize,
          direction,
          pageDiff,
          paginationId,
          searchQuery,
          // taskId,
          // issueId,
        } = params;

        // Still calling logHoursApis.listPhases as per instructions
        const apiResponse: any = await logHoursApis.listTimeLog({
          // page: current,
          pageSize,
          searchQuery: searchQuery || undefined,
          direction,
          sortOrder,
          pageDiff,
          paginationId,
          projectIds: projectId,
          taskIds: taskId ? taskId : undefined,
          issueIds: issueId ? issueId : undefined,
          // derivedProjectStatus: extra?.screenName,
        });

        const { data, status } = apiResponse || {};
        if (status) {
          // Only dispatch if this is a refresh triggered by time log updates
          // dispatch(updateTimeLogInDetailModalSlice({ isUpdated: true }));
          const { list, count } =
            data || apiResponse?.response.data || apiResponse.response;

          if (list?.length < pageSize) {
            setEndOfList(true);
          }
          setPagination((prev) => ({
            ...prev,
            total: Number(count),
          }));
          if (list) {
            setDataSource((prev: any) =>
              append ? [...prev, ...mapTimeLogList(list)] : mapTimeLogList(list)
            );
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    },
    [projectId, taskId, issueId, dispatch]
  );

  // Replace the mock refreshData with actual API call
  const refreshData = React.useCallback(
    async (newSearchQuery?: string | null) => {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      await fetchData({
        // current: pagination.current,
        current: 1,
        pageSize: pagination.pageSize,
        sortOrder: "DESC",
        direction: "P",
        pageDiff: 0,
        paginationId: null,
        projectId,
        taskId,
        issueId,
        searchQuery:
          newSearchQuery !== undefined ? newSearchQuery : searchQuery,
      });
    },
    [pagination.pageSize, projectId, taskId, issueId, searchQuery, fetchData]
  );

  // useEffect(() => {
  //   fetchData({
  //     current: 1,
  //     pageSize: pagination.pageSize,
  //     sortOrder: "DESC",
  //     direction: "P",
  //     pageDiff: 0,
  //     paginationId: null,
  //   });
  // }, [taskId, issueId, projectIds]);

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction === "F"
            ? dataSource[dataSource.length - 1]?.timeLogId
            : dataSource[0]?.timeLogId;

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
    // refreshData();
    fetchData({
      current: 1,
      pageSize: pagination.pageSize,
      sortOrder: "DESC",
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      projectIds: projectId,
      taskId,
      issueId,
    });
  }, [location, taskId, issueId, projectId]);

  // if (loading)
  //   return (
  //     <>
  //       <Head title={"Project TimeSheets"} />
  //       <Content>
  //         <Card className={"card-bordered"}>
  //           <div className="card-aside-wrap">
  //             <div
  //               className={"card-inner card-inner-lg"}
  //               style={{
  //                 minHeight: "87vh",
  //                 padding: undefined,
  //               }}
  //             >
  //               <h5>{projectName}</h5>
  //               <Flex align="center" gap="middle" className="pageLoader">
  //                 <Spin
  //                   indicator={<LoadingOutlined spin />}
  //                   className="m-auto"
  //                   size="large"
  //                 />
  //               </Flex>
  //             </div>
  //           </div>
  //         </Card>
  //       </Content>
  //     </>
  //   );

  const toolbar = (
    // TimeSheetsHeader lays out its own left/right split — let it take the
    // full toolbar width.
    <div style={{ flex: 1 }}>
      <TimeSheetsHeader
        projectName={projectName}
        projectId={projectId}
        listViewType={listViewType}
        setListViewType={setListViewType}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        groupByOptions={groupByOptions}
        getLabel={getLabel}
        showAddTimeLogButton={listViewType === "list"}
        onAddTimeLogClick={() => setModalOpen(true)}
        searchQuery={searchQuery}
        refreshData={refreshData}
        onSearchChange={(value) => {
          setsearchQuery(value || null);
          setPagination({
            ...pagination,
            current: 1,
          });
          refreshData(value || null);
        }}
      />
    </div>
  );

  return (
    <>
      <ProjectPageShell
        headTitle="Time Sheets"
        breadcrumb={[{ label: projectName }, { label: "Time Sheets" }]}
        toolbar={toolbar}
      >
        {listViewType === "list" && (
          <SkeletonTable
            loading={loading}
            columns={columns as SkeletonTableColumnsType[]}
          >
            <Table
              rowKey="timeLogId"
              className="pms-hub-table"
              columns={columns}
              dataSource={dataSource}
              pagination={pagination}
              onChange={tableOnChange}
              size="middle"
              scroll={{ x: 1400 }}
            />
          </SkeletonTable>
        )}
      </ProjectPageShell>

      <TimeSheetListCreation
        projectId={projectId}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        refreshData={refreshData}
        linkedTask={taskId}
        linkedIssue={issueId}
      />
    </>
  );
};

export default TimeSheetsListView;
